import { useState, useCallback, useEffect, useRef } from 'react';
import DashboardService, {
  DashboardData,
  DateRange,
  DataStatus,
} from '../services/DashboardService';
import { useRetry } from './useRetry';
import { getWeekLabel } from '../utils/formatters';
import { getReportTypesForFileType } from '../utils/cacheManager';

interface UseDashboardDataProps {
  viewMode: 'weekly' | 'quarterly';
  sortBy: string;
}

export const useDashboardData = ({ viewMode, sortBy }: UseDashboardDataProps) => {
  const { fetchWithRetry } = useRetry();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [dateRanges, setDateRanges] = useState<DateRange[]>([]);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange | null>(null);
  const [dataStatus, setDataStatus] = useState<DataStatus | null>(null);
  const [availableQuarters, setAvailableQuarters] = useState<string[]>([]);
  const [selectedQuarter, setSelectedQuarter] = useState<{ quarter: string; year: number } | null>(null);

  const selectedDateRangeRef = useRef<DateRange | null>(null);
  const selectedQuarterRef = useRef<{ quarter: string; year: number } | null>(null);
  const viewModeRef = useRef<'weekly' | 'quarterly'>('weekly');

  // Keep refs in sync with state
  useEffect(() => {
    selectedDateRangeRef.current = selectedDateRange;
  }, [selectedDateRange]);

  useEffect(() => {
    selectedQuarterRef.current = selectedQuarter;
  }, [selectedQuarter]);

  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  const selectFirstCompleteWeeklyRange = useCallback(
    async (ranges: DateRange[]): Promise<boolean> => {
      for (const range of ranges) {
        try {
          const status = await fetchWithRetry(() =>
            DashboardService.getDataStatus(range.startDate, range.endDate)
          );
          if (status?.allDataAvailable) {
            setSelectedDateRange({
              ...range,
              label: getWeekLabel(range.startDate),
            });
            setDataStatus(status);
            return true;
          }
        } catch (err) {
          console.warn('Failed to check data status for range', range, err);
        }
      }
      return false;
    },
    [fetchWithRetry]
  );

  const fetchDateRanges = useCallback(
    async (preserveSelection: boolean = false) => {
      setLoading(true);
      try {
        const ranges = await fetchWithRetry(() => DashboardService.getAvailableDateRanges());
        setDateRanges(ranges);
        if (ranges.length > 0) {
          const currentSelection = selectedDateRangeRef.current;
          if (preserveSelection && currentSelection) {
            const existingRange = ranges.find(
              (r) => r.startDate === currentSelection.startDate && r.endDate === currentSelection.endDate
            );
            if (existingRange) {
              setSelectedDateRange({
                ...existingRange,
                label: getWeekLabel(existingRange.startDate),
              });
              setLoading(false);
              return;
            }
          }

          const foundComplete = await selectFirstCompleteWeeklyRange(ranges);
          if (!foundComplete) {
            const firstRange = {
              ...ranges[0],
              label: getWeekLabel(ranges[0].startDate),
            };
            setSelectedDateRange(firstRange);
          }
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch date ranges');
      } finally {
        const currentSelection = selectedDateRangeRef.current;
        if (!preserveSelection || !currentSelection) {
          setLoading(false);
        }
      }
    },
    [fetchWithRetry, selectFirstCompleteWeeklyRange]
  );

  const fetchDashboardData = useCallback(async () => {
    if (!selectedDateRange) return;

    setLoading(true);
    setError(null);
    try {
      const data = await fetchWithRetry(() =>
        DashboardService.getDashboardData(selectedDateRange.startDate, selectedDateRange.endDate, sortBy)
      );
      setDashboardData(data);

      try {
        const status = await fetchWithRetry(() =>
          DashboardService.getDataStatus(selectedDateRange.startDate, selectedDateRange.endDate)
        );
        setDataStatus(status);
      } catch (statusErr: any) {
        console.warn('Failed to fetch data status:', statusErr);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  }, [fetchWithRetry, selectedDateRange, sortBy]);

  const fetchDataStatus = useCallback(async () => {
    if (!selectedDateRange) return;

    try {
      const status = await fetchWithRetry(() =>
        DashboardService.getDataStatus(selectedDateRange.startDate, selectedDateRange.endDate)
      );
      setDataStatus(status);
    } catch (err: any) {
      console.warn('Failed to fetch data status:', err);
      setDataStatus(null);
    }
  }, [fetchWithRetry, selectedDateRange]);

  const fetchQuarterlyDataStatus = useCallback(async () => {
    if (!selectedQuarter) return;

    try {
      const status = await fetchWithRetry(() =>
        DashboardService.getQuarterlyDataStatus(selectedQuarter.quarter, selectedQuarter.year)
      );
      setDataStatus(status);
    } catch (err: any) {
      console.warn('Failed to fetch quarterly data status:', err);
      setDataStatus(null);
    }
  }, [fetchWithRetry, selectedQuarter]);

  const fetchQuarters = useCallback(async () => {
    try {
      const quarterYears = await fetchWithRetry(() => DashboardService.getAvailableQuarters());
      setAvailableQuarters(quarterYears);
      if (quarterYears.length > 0 && !selectedQuarter) {
        // Default to most recent quarter-year (first in list, already sorted)
        // Backend returns format "Q2 2025" (space-separated), not "Q2-2025"
        const quarterStr = quarterYears[0].trim();
        const match = quarterStr.match(/^(Q[1-4])\s+(\d{4})$/i);
        if (match) {
          const [, quarter, yearStr] = match;
          const year = parseInt(yearStr, 10);
          if (!isNaN(year)) {
            setSelectedQuarter({ quarter, year });
          }
        } else {
          // Fallback: try splitting by space or dash
          const parts = quarterStr.split(/\s+|-/);
          if (parts.length >= 2) {
            const quarter = parts[0];
            const yearStr = parts[parts.length - 1];
            const year = parseInt(yearStr, 10);
            if (!isNaN(year)) {
              setSelectedQuarter({ quarter, year });
            }
          }
        }
      }
    } catch (err: any) {
      console.warn('Failed to fetch quarters:', err);
    }
  }, [fetchWithRetry, selectedQuarter]);

  const fetchQuarterlyData = useCallback(async () => {
    if (!selectedQuarter || isNaN(selectedQuarter.year)) {
      console.warn('Cannot fetch quarterly data: invalid quarter or year', selectedQuarter);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await fetchWithRetry(() =>
        DashboardService.getQuarterlyDashboardData(selectedQuarter.quarter, selectedQuarter.year, sortBy)
      );
      setDashboardData(data);

      try {
        const status = await fetchWithRetry(() =>
          DashboardService.getQuarterlyDataStatus(selectedQuarter.quarter, selectedQuarter.year)
        );
        setDataStatus(status);
      } catch (statusErr: any) {
        console.warn('Failed to fetch quarterly data status:', statusErr);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch quarterly dashboard data');
    } finally {
      setLoading(false);
    }
  }, [fetchWithRetry, selectedQuarter, sortBy]);

  // Set up refs for file change listener
  const fetchDateRangesRef = useRef(fetchDateRanges);
  const fetchQuartersRef = useRef(fetchQuarters);
  const fetchDashboardDataRef = useRef(fetchDashboardData);
  const fetchQuarterlyDataRef = useRef(fetchQuarterlyData);

  useEffect(() => {
    fetchDateRangesRef.current = fetchDateRanges;
  }, [fetchDateRanges]);

  useEffect(() => {
    fetchQuartersRef.current = fetchQuarters;
  }, [fetchQuarters]);

  useEffect(() => {
    fetchDashboardDataRef.current = fetchDashboardData;
  }, [fetchDashboardData]);

  useEffect(() => {
    fetchQuarterlyDataRef.current = fetchQuarterlyData;
  }, [fetchQuarterlyData]);

  // File change listener with smart invalidation
  useEffect(() => {
    const handleFilesChanged = (event: CustomEvent<{ fileType?: string }>) => {
      const fileType = event.detail?.fileType;
      const currentViewMode = viewModeRef.current;
      const currentDateRange = selectedDateRangeRef.current;
      const currentQuarter = selectedQuarterRef.current;

      // If file type is provided, check if it affects current view
      if (fileType) {
        const affectedReportTypes = getReportTypesForFileType(fileType as any);
        const shouldRefreshWeekly = affectedReportTypes.includes('weekly') || affectedReportTypes.includes('dateRanges');
        const shouldRefreshQuarterly = affectedReportTypes.includes('quarterly') || affectedReportTypes.includes('quarters');

        console.log(`Files changed (${fileType}), affected report types:`, affectedReportTypes);
        
        if (currentViewMode === 'weekly' && shouldRefreshWeekly) {
          console.log('Refreshing weekly data due to file change');
          fetchDateRangesRef.current(true);
          if (currentDateRange) {
            fetchDashboardDataRef.current();
          }
        } else if (currentViewMode === 'quarterly' && shouldRefreshQuarterly) {
          console.log('Refreshing quarterly data due to file change');
          fetchQuartersRef.current();
          if (currentQuarter) {
            fetchQuarterlyDataRef.current();
          }
        } else {
          console.log(`File type ${fileType} does not affect current view (${currentViewMode}), skipping refresh`);
        }
      } else {
        // No file type info - refresh everything (backwards compatibility)
        console.log('Files changed (no file type info), refreshing all data...');
        if (currentViewMode === 'weekly') {
          fetchDateRangesRef.current(true);
          if (currentDateRange) {
            fetchDashboardDataRef.current();
          }
        } else {
          fetchQuartersRef.current();
          if (currentQuarter) {
            fetchQuarterlyDataRef.current();
          }
        }
      }
    };

    window.addEventListener('filesChanged', handleFilesChanged as EventListener);
    return () => {
      window.removeEventListener('filesChanged', handleFilesChanged as EventListener);
    };
  }, []);

  // Initial data fetch
  useEffect(() => {
    if (viewMode === 'weekly') {
      fetchDateRanges();
    } else {
      fetchQuarters();
    }
  }, [viewMode, fetchDateRanges, fetchQuarters]);

  // Data status fetch
  useEffect(() => {
    if (viewMode === 'weekly' && selectedDateRange) {
      fetchDataStatus();
    } else if (viewMode === 'quarterly' && selectedQuarter) {
      fetchQuarterlyDataStatus();
    } else {
      setDataStatus(null);
    }
  }, [selectedDateRange, selectedQuarter, viewMode, fetchDataStatus, fetchQuarterlyDataStatus]);

  // Dashboard data fetch
  useEffect(() => {
    if (viewMode === 'weekly' && selectedDateRange) {
      fetchDashboardData();
    } else if (viewMode === 'quarterly' && selectedQuarter) {
      fetchQuarterlyData();
    }
  }, [selectedDateRange, selectedQuarter, sortBy, viewMode, fetchDashboardData, fetchQuarterlyData]);

  return {
    loading,
    error,
    dashboardData,
    dateRanges,
    selectedDateRange,
    setSelectedDateRange,
    dataStatus,
    setDataStatus,
    availableQuarters,
    selectedQuarter,
    setSelectedQuarter,
    setError,
  };
};

